/* ============================================================
   SAM-592 — event size price bands. Events (Single-day / Multi-day Event subtypes) are charged
   by event size, not per ticket: `settings.event_size` (s/m/l/xl) is suggested from the
   service's max capacity and editable; `pricing.price_unit: "event"` plus per-option
   `prices_by_event_size` drive the price. Keep the bands in sync with
   sam-lambda-functions/functions/booking/pricing.py::EVENT_SIZE_BANDS.
   ============================================================ */

export type EventSize = 's' | 'm' | 'l' | 'xl';

export interface EventSizeBand {
  key: EventSize;
  label: string;
  min: number;
  /** null = unbounded (500+). */
  max: number | null;
}

export const EVENT_SIZE_BANDS: readonly EventSizeBand[] = [
  { key: 's', label: '1–50 pax', min: 1, max: 50 },
  { key: 'm', label: '51–150 pax', min: 51, max: 150 },
  { key: 'l', label: '151–500 pax', min: 151, max: 500 },
  { key: 'xl', label: '500+ pax', min: 501, max: null },
];

export const EVENT_SUBTYPES: readonly string[] = ['single', 'multiday'];

export function isEventSubtype(subtype: string | null | undefined): boolean {
  return !!subtype && EVENT_SUBTYPES.includes(subtype);
}

export function isEventSize(value: unknown): value is EventSize {
  return EVENT_SIZE_BANDS.some((b) => b.key === value);
}

/** Band containing `pax`, or null for a missing / non-positive / non-numeric value. */
export function eventSizeForPax(pax: number | string | null | undefined): EventSize | null {
  const n = typeof pax === 'string' ? (pax.trim() === '' ? NaN : Number(pax)) : pax;
  if (n == null || !Number.isFinite(n) || n < 1) return null;
  const whole = Math.floor(n);
  return EVENT_SIZE_BANDS.find((b) => whole >= b.min && (b.max === null || whole <= b.max))?.key ?? null;
}

/** The suggestion shown in Scheduling & Capacity: the band containing max capacity. */
export function suggestEventSize(maxCapacity: number | string | null | undefined): EventSize | null {
  return eventSizeForPax(maxCapacity);
}
